/**
 * Hook centralisant toute la logique métier de la page Détail facture fournisseur.
 * Extrait pour rendre FactureFournisseurDetail.jsx purement présentational.
 *
 * Returns : { f, loading, saving, ocrRunning, pageBusy,
 *             handlers (handleSave, handleOCR, handleValider, handleRejeter,
 *             handleRegenerer, handlePayer, handleDevalider, handleRevertExport,
 *             handleAddNoteCredit, handleDeleteNoteCredit, rotateFile, deleteFile,
 *             moveFile, mergeToPdf),
 *             updaters (updateField, updateMontant, updateTauxTva),
 *             reload }
 */
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";

const STATUT_LABELS = {
  brouillon: "Brouillon", a_valider: "À valider", validee: "Validée",
  a_payer: "À payer", payee: "Payée", rejetee: "Rejetée", a_exporter: "À exporter",
  exportee: "Exportée",
};

export function useFFActions(id) {
  const navigate = useNavigate();
  const [f, setF] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [ocrRunning, setOcrRunning] = useState(false);
  const [pageBusy, setPageBusy] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/factures-fournisseurs/${id}`);
      setF(data);
    } catch {
      toast.error("Facture introuvable");
      navigate("/factures-fournisseurs");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { reload(); }, [reload]);

  // ---- Updaters de form ----
  const updateField = (k, v) => setF((prev) => ({ ...prev, [k]: v }));

  const updateMontant = (champ, value) => {
    const v = value === "" ? "" : parseFloat(value);
    setF((prev) => {
      const next = { ...prev, [champ]: value };
      const taux = parseFloat(prev.tva_taux) || 0;
      if (Number.isNaN(v) || v === "") return next;
      if (champ === "montant_ht" && taux > 0) {
        const tva = +(v * taux / 100).toFixed(2);
        next.tva_montant = tva;
        next.montant_ttc = +(v + tva).toFixed(2);
      } else if (champ === "montant_ttc" && taux > 0) {
        const ht = +(v / (1 + taux / 100)).toFixed(2);
        next.montant_ht = ht;
        next.tva_montant = +(v - ht).toFixed(2);
      } else if (champ === "tva_montant") {
        const ht = parseFloat(prev.montant_ht) || 0;
        if (ht > 0) next.montant_ttc = +(ht + v).toFixed(2);
      }
      return next;
    });
  };

  const updateTauxTva = (newTaux) => {
    const taux = parseFloat(newTaux) || 0;
    setF((prev) => {
      const next = { ...prev, tva_taux: taux };
      const ht = parseFloat(prev.montant_ht) || 0;
      const ttc = parseFloat(prev.montant_ttc) || 0;
      if (ht > 0 && taux > 0) {
        const tva = +(ht * taux / 100).toFixed(2);
        next.tva_montant = tva;
        next.montant_ttc = +(ht + tva).toFixed(2);
      } else if (ttc > 0 && taux > 0) {
        const htCalc = +(ttc / (1 + taux / 100)).toFixed(2);
        next.montant_ht = htCalc;
        next.tva_montant = +(ttc - htCalc).toFixed(2);
      } else if (taux === 0) {
        next.tva_montant = 0;
      }
      return next;
    });
  };

  // ---- Save (avec gestion auto-régénération écritures) ----
  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        fournisseur_nom: f.fournisseur_nom,
        fournisseur_iban: f.fournisseur_iban_edit !== undefined ? f.fournisseur_iban_edit : undefined,
        numero_facture: f.numero_facture, date_facture: f.date_facture, date_echeance: f.date_echeance,
        montant_ht: f.montant_ht ? parseFloat(f.montant_ht) : null,
        tva_taux: f.tva_taux ? parseFloat(f.tva_taux) : null,
        tva_montant: f.tva_montant ? parseFloat(f.tva_montant) : null,
        montant_ttc: f.montant_ttc ? parseFloat(f.montant_ttc) : null,
        devise: f.devise, qr_reference: f.qr_reference, rf_reference: f.rf_reference,
        description: f.description, compte_charge: f.compte_charge, compte_tva: f.compte_tva,
        notes: f.notes,
      };
      Object.keys(payload).forEach((k) => payload[k] === undefined && delete payload[k]);
      const { data } = await api.patch(`/factures-fournisseurs/${id}`, payload);
      setF({ ...data, fournisseur_iban_edit: undefined });
      const regen = data._ecritures_regen;
      if (regen?.regenere) {
        toast.success(
          `Modifications enregistrées · Compta mise à jour automatiquement ` +
          `(HT ${regen.ht?.toFixed?.(2)} · TVA ${regen.tva?.toFixed?.(2)} · TTC ${regen.ttc?.toFixed?.(2)})`,
          { duration: 5000 },
        );
      } else if (regen && !regen.regenere) {
        toast.warning(
          `Modifications enregistrées · ⚠️ Compta non régénérée (${regen.raison}). Vérifiez manuellement.`,
          { duration: 6000 },
        );
      } else {
        toast.success("Modifications enregistrées");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const handleOCR = async (force = false) => {
    setOcrRunning(true);
    try {
      const url = force ? `/factures-fournisseurs/${id}/ocr?force=true` : `/factures-fournisseurs/${id}/ocr`;
      const { data } = await api.post(url);
      setF(data);
      const conf = data.donnees_ocr?.confiance;
      toast.success(`Analyse OCR terminée (confiance : ${Math.round((conf || 0) * 100)} %)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur OCR");
    } finally {
      setOcrRunning(false);
    }
  };

  const handleValider = async () => {
    if (!window.confirm(
      `Valider cette facture ?\n\n` +
      `Fournisseur : ${f.fournisseur_nom}\nMontant : ${f.montant_ttc} ${f.devise || "CHF"}\n` +
      `Compte : ${f.compte_charge}\n\nLes écritures comptables (charges + TVA préalable / créanciers) seront créées automatiquement.`
    )) return;
    try {
      const { data } = await api.post(`/factures-fournisseurs/${id}/valider`);
      setF(data);
      toast.success(`Facture validée — statut : ${STATUT_LABELS[data.statut]}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Validation impossible");
    }
  };

  const handleRejeter = async () => {
    const motif = window.prompt("Motif du rejet :", "Facture non conforme");
    if (motif === null) return;
    try {
      const { data } = await api.post(`/factures-fournisseurs/${id}/rejeter`, { motif });
      // iter91 — Protection contre les réponses null/undefined (anti-crash UI)
      if (data && typeof data === "object") {
        setF(data);
      }
      toast.success("Facture rejetée");
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Erreur lors du rejet";
      toast.error(msg);
    }
  };

  const handleRegenerer = async () => {
    if (!window.confirm(
      `Régénérer les écritures comptables ?\n\nLes écritures actuelles seront supprimées et recréées avec les nouveaux montants :\n` +
      `  HT : ${f.montant_ht} · TVA ${f.tva_taux}% : ${f.tva_montant} · TTC : ${f.montant_ttc}\n  Compte charge : ${f.compte_charge}`
    )) return;
    try {
      await handleSave();
      const { data } = await api.post(`/factures-fournisseurs/${id}/regenerer-ecritures`);
      setF(data);
      toast.success("Écritures comptables régénérées avec succès");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Régénération impossible");
    }
  };

  const handlePayer = async (paiement) => {
    try {
      const { data } = await api.post(`/factures-fournisseurs/${id}/payer`, {
        date: paiement.date, montant: parseFloat(paiement.montant),
        mode: paiement.mode, note: paiement.note || null,
      });
      setF(data);
      toast.success("Paiement enregistré · écriture journal créée");
      return data;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur paiement");
      throw e;
    }
  };

  const handleDevalider = async (motif) => {
    try {
      const { data } = await api.post(`/factures-fournisseurs/${id}/devalider`, {
        motif: motif || "Retour à 'à valider' pour correction",
      });
      setF(data);
      toast.success("Facture repassée en 'À valider' — écritures comptables supprimées");
      return data;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Dévalidation échouée");
      throw e;
    }
  };

  const handleRevertExport = async (motif) => {
    try {
      const { data } = await api.post(`/factures-fournisseurs/${id}/revert-export`, { motif });
      setF(data);
      toast.success("Facture repassée en 'À payer'");
      return data;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
      throw e;
    }
  };

  // iter342 — Annulation complète (admin) : facture + écritures + lot d'export supprimés
  const handleAnnulerDefinitivement = async (motif) => {
    try {
      const { data } = await api.post(`/factures-fournisseurs/${id}/annuler-definitivement`, { motif });
      toast.success(
        `Facture annulée · ${data.nb_ecritures_supprimees} écriture(s) supprimée(s)` +
        (data.retire_du_lot_export ? " · retirée du lot d'export" : ""),
      );
      if (data.avertissement) toast.warning(data.avertissement, { duration: 12000 });
      navigate("/factures-fournisseurs");
      return data;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Annulation impossible", { duration: 10000 });
      throw e;
    }
  };

  const handleAddNoteCredit = async (nc) => {
    const montant = parseFloat(nc.montant);
    if (!montant || montant <= 0) {
      toast.error("Montant invalide");
      throw new Error("invalid");
    }
    const fd = new FormData();
    fd.append("montant", String(montant));
    if (nc.commentaire) fd.append("commentaire", nc.commentaire);
    if (nc.fichier) fd.append("fichier", nc.fichier);
    try {
      await api.post(`/factures-fournisseurs/${id}/notes-credit`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Note de crédit de ${montant.toFixed(2)} CHF ajoutée`);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
      throw e;
    }
  };

  const handleDeleteNoteCredit = async (ncId, montant) => {
    if (!window.confirm(`Supprimer la note de crédit de ${montant.toFixed(2)} CHF ?\n\nLe reste à payer sera recalculé en conséquence.`)) return;
    try {
      await api.delete(`/factures-fournisseurs/${id}/notes-credit/${ncId}`);
      toast.success("Note de crédit supprimée");
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  // ---- Page management ----
  const rotateFile = async (idx, degrees) => {
    setPageBusy(true);
    try {
      const { data } = await api.post(`/factures-fournisseurs/${id}/files/${idx}/rotate`, { degrees });
      setF(data); toast.success(`Page tournée de ${degrees}°`);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur rotation"); }
    finally { setPageBusy(false); }
  };
  const deleteFile = async (idx) => {
    if (!window.confirm("Supprimer cette page ? Cette action est irréversible.")) return;
    setPageBusy(true);
    try {
      const { data } = await api.delete(`/factures-fournisseurs/${id}/files/${idx}`);
      setF(data); toast.success("Page supprimée");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setPageBusy(false); }
  };
  const moveFile = async (idx, dir) => {
    const fichiers = f?.fichiers || [];
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= fichiers.length) return;
    const order = fichiers.map((_, i) => i);
    [order[idx], order[newIdx]] = [order[newIdx], order[idx]];
    setPageBusy(true);
    try {
      const { data } = await api.post(`/factures-fournisseurs/${id}/files/reorder`, { order });
      setF(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setPageBusy(false); }
  };
  const mergeToPdf = async () => {
    const imgCount = (f?.fichiers || []).filter((fic) => /^image\//.test(fic.mime || "")).length;
    if (imgCount < 2) { toast.error("Au moins 2 images sont requises pour fusionner"); return; }
    if (!window.confirm(`Fusionner ${imgCount} image(s) en 1 PDF unifié ? Les images d'origine seront remplacées.`)) return;
    setPageBusy(true);
    try {
      const { data } = await api.post(`/factures-fournisseurs/${id}/files/merge-to-pdf`);
      setF(data); toast.success("Images fusionnées en PDF");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur fusion"); }
    finally { setPageBusy(false); }
  };

  return {
    f, setF, loading, saving, ocrRunning, pageBusy, reload,
    updateField, updateMontant, updateTauxTva,
    handleSave, handleOCR, handleValider, handleRejeter, handleRegenerer,
    handlePayer, handleDevalider, handleRevertExport, handleAnnulerDefinitivement,
    handleAddNoteCredit, handleDeleteNoteCredit,
    rotateFile, deleteFile, moveFile, mergeToPdf,
  };
}

export { STATUT_LABELS };
